import firebase from "config/firebase";

const PAGE_SIZE = 5;

// ─── Boost user profile (for profile modal) ───────────────────────────────────

export const fetchBoostUserProfile = (uid, isManager) => async (dispatch) => {
  dispatch({ type: "BOOST_PROFILE_LOADING" });
  try {
    if (isManager) {
      const docSnap = await firebase.firestore().collection("users").doc(uid).get();
      dispatch({
        type: "BOOST_PROFILE_SUCCESS",
        payload: docSnap.exists ? { id: docSnap.id, ...docSnap.data() } : null,
      });
    } else {
      const snap = await firebase
        .firestore()
        .collection("users")
        .where("athleteID", "==", uid)
        .limit(1)
        .get();

      if (!snap.empty) {
        dispatch({ type: "BOOST_PROFILE_SUCCESS", payload: { id: snap.docs[0].id, ...snap.docs[0].data() } });
      } else {
        const docSnap = await firebase.firestore().collection("users").doc(uid).get();
        dispatch({
          type: "BOOST_PROFILE_SUCCESS",
          payload: docSnap.exists ? { id: docSnap.id, ...docSnap.data() } : null,
        });
      }
    }
  } catch (e) {
    console.error("fetchBoostUserProfile error", e);
    dispatch({ type: "BOOST_PROFILE_SUCCESS", payload: null });
  }
};

export const clearBoostProfile = () => ({ type: "BOOST_PROFILE_CLEAR" });

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatRemainingTime = (expiryTimestamp) => {
  const now = Date.now();
  const expiry = expiryTimestamp.toDate().getTime();
  const diffMs = expiry - now;
  if (diffMs <= 0) return "Expired";
  const totalMinutes = Math.floor(diffMs / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  return `${days}d ${hours}h`;
};

const mapDoc = (doc) => {
  const d = doc.data();
  const fanCount = d.boostedBy?.length ?? 0;
  const totalAmountSpent = d.totalAmountSpent || 0;
  return {
    id: doc.id,
    name: d.name || "N/A",
    email: d.email || "N/A",
    totalBoosts: d.totalBoosts || 0,
    fans: `${fanCount} ${fanCount === 1 ? "fan" : "fans"}`,
    amountSpent: `${Number(totalAmountSpent).toFixed(2)}`,
    _amountSpentRaw: totalAmountSpent,
    remainingTime: formatRemainingTime(d.expiryTime),
    featureType: d.featureType,
    _lastBoostMs: d.lastBoostTime?.toDate?.().getTime() ?? 0,
    _uid: doc.id.split("_")[0],
    // keep the raw Firestore doc for cursor-based pagination
    _rawDoc: doc,
  };
};

// Sort by totalAmountSpent DESC, then lastBoostTime DESC
const sortList = (list) =>
  [...list].sort((a, b) => {
    if (b._amountSpentRaw !== a._amountSpentRaw)
      return b._amountSpentRaw - a._amountSpentRaw;
    return b._lastBoostMs - a._lastBoostMs;
  });

const enrichWithUserProfile = async (entry, isManager) => {
  try {
    const db = firebase.firestore();
    let profile = null;

    if (isManager) {
      const docSnap = await db.collection("users").doc(entry._uid).get();
      if (docSnap.exists) profile = { id: docSnap.id, ...docSnap.data() };
    } else {
      const snap = await db
        .collection("users")
        .where("athleteID", "==", entry._uid)
        .limit(1)
        .get();

      if (!snap.empty) {
        profile = { id: snap.docs[0].id, ...snap.docs[0].data() };
      } else {
        const docSnap = await db.collection("users").doc(entry._uid).get();
        if (docSnap.exists) profile = { id: docSnap.id, ...docSnap.data() };
      }
    }

    return profile ? { ...profile, ...entry } : entry;
  } catch (e) {
    console.error("enrichWithUserProfile error", e);
    return entry;
  }
};

// ─── Base query builder ───────────────────────────────────────────────────────

const buildBaseQuery = (featureTypes, now) => {
  // Firestore doesn't support OR on the same field in a single query,
  // so we fetch all active boosts and filter by featureType client-side.
  // The query is ordered by expiryTime so we can paginate with startAfter.
  return firebase
    .firestore()
    .collection("boosts")
    .where("expiryTime", ">", now)
    .orderBy("expiryTime", "asc");
};

// ─── Fetch ALL active docs for one tab, sorted by totalAmountSpent DESC ───────

/**
 * Fetches all active boost docs matching `featureTypes`, sorts by
 * totalAmountSpent DESC (then lastBoostTime DESC), and returns a page slice.
 *
 * Returns { entries, nextOffset, hasMore }
 */
const fetchPage = async (featureTypes, now, offset = 0) => {
  // Fetch all active boosts — Firestore range filter requires orderBy on the
  // same field first, so we can't push totalAmountSpent into the DB sort.
  // We fetch everything and sort client-side to guarantee correct ordering.
  const snap = await firebase
    .firestore()
    .collection("boosts")
    .where("expiryTime", ">", now)
    .orderBy("expiryTime", "asc")
    .get();

  // Filter by featureType and map
  const all = snap.docs
    .filter((doc) => featureTypes.includes(doc.data().featureType))
    .map(mapDoc);

  // Sort by totalAmountSpent DESC, then lastBoostTime DESC
  const sorted = sortList(all);

  // Slice the requested page
  const page = sorted.slice(offset, offset + PAGE_SIZE);
  const nextOffset = offset + page.length;
  const hasMore = nextOffset < sorted.length;

  return { entries: page, nextOffset, hasMore };
};

// ─── Initial load ─────────────────────────────────────────────────────────────

export const fetchBulletinBoard = () => async (dispatch) => {
  dispatch({ type: "BULLETIN_BOARD_LOADING" });

  try {
    const now = firebase.firestore.Timestamp.now();

    const [athleteResult, managerResult, shoutoutResult] = await Promise.all([
      fetchPage(["boost"], now, 0),
      fetchPage(["promoteManager", "selfBoost"], now, 0),
      fetchPage(["shoutout"], now, 0),
    ]);

    // Enrich with user profiles in parallel
    const [boostedAthletes, boostedManagers, shoutOutAthletes] = await Promise.all([
      Promise.all(athleteResult.entries.map((e) => enrichWithUserProfile(e, false))),
      Promise.all(managerResult.entries.map((e) => enrichWithUserProfile(e, true))),
      Promise.all(shoutoutResult.entries.map((e) => enrichWithUserProfile(e, false))),
    ]);

    dispatch({
      type: "BULLETIN_BOARD_SUCCESS",
      payload: {
        boostedAthletes,
        boostedManagers,
        shoutOutAthletes,
        _lastDocs: {
          boostedAthletes: athleteResult.nextOffset,
          boostedManagers: managerResult.nextOffset,
          shoutOutAthletes: shoutoutResult.nextOffset,
        },
        hasMore: {
          boostedAthletes: athleteResult.hasMore,
          boostedManagers: managerResult.hasMore,
          shoutOutAthletes: shoutoutResult.hasMore,
        },
      },
    });
  } catch (e) {
    console.error("fetchBulletinBoard error", e);
    dispatch({ type: "BULLETIN_BOARD_ERROR" });
  }
};

// ─── Load more ────────────────────────────────────────────────────────────────

/**
 * tab: "boostedAthletes" | "boostedManagers" | "shoutOutAthletes"
 */
export const loadMoreBulletinBoard = (tab) => async (dispatch, getState) => {
  dispatch({ type: "BULLETIN_BOARD_LOAD_MORE_START" });

  try {
    const now = firebase.firestore.Timestamp.now();
    const offset = getState().bulletinBoard._lastDocs[tab] ?? 0;

    const featureTypesMap = {
      boostedAthletes: ["boost"],
      boostedManagers: ["promoteManager", "selfBoost"],
      shoutOutAthletes: ["shoutout"],
    };
    const isManagerMap = {
      boostedAthletes: false,
      boostedManagers: true,
      shoutOutAthletes: false,
    };

    const { entries, nextOffset, hasMore } = await fetchPage(
      featureTypesMap[tab],
      now,
      offset
    );

    const enriched = await Promise.all(
      entries.map((e) => enrichWithUserProfile(e, isManagerMap[tab]))
    );

    dispatch({
      type: "BULLETIN_BOARD_LOAD_MORE_SUCCESS",
      payload: {
        tab,
        newItems: enriched,
        lastDoc: nextOffset,
        hasMore,
      },
    });
  } catch (e) {
    console.error("loadMoreBulletinBoard error", e);
    dispatch({ type: "BULLETIN_BOARD_ERROR" });
  }
};

// Keep old export name as alias so nothing else breaks
export const subscribeBulletinBoard = fetchBulletinBoard;
